import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Download, Award, Trophy, CheckCircle2, Eye, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from '../hooks/use-toast';

const CertificateDownload = ({ userProgress, stages }) => {
  const [showCertificate, setShowCertificate] = useState(false);
  
  const completedStages = stages.filter(stage => 
    userProgress.completedStages.includes(stage.id)
  );
  
  const completionPercentage = Math.round(
    (completedStages.length / stages.length) * 100
  );

  const generateCertificateText = () => {
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
        BENEFICIARIES BLUEPRINT
        CERTIFICATE OF COMPLETION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This certifies that the bearer has successfully completed
the Beneficiaries Blueprint educational program and has
mastered the Reclaimation Process pioneered by
Empress Ninti El Bey.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

COMPLETION SUMMARY:

Date Completed: ${date}
Total Score: ${userProgress.totalScore} points
Completion Rate: ${completionPercentage}%
Stages Completed: ${completedStages.length}/${stages.length}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

KNOWLEDGE MASTERED:

✓ The Foundation
  - Understanding Empress Ninti El Bey's pioneering work
  - Historical foundation of the Reclaimation Process
  - Path to freedom for all Moors and indigenous inhabitants

✓ Lawful Notice
  - Notice by Publication
  - Actual Notice
  - Constructive Notice
  - Proper filing at Library of Congress, county & court records

✓ Status Correction
  - Correcting legal status from corporate to sovereign
  - Available to all seeking freedom from corporate status
  - Filing requirements and procedures

✓ Trust Creation
  - Establishing trusts for reclaimed assets
  - Protecting and managing wealth
  - Creating trust funds for beneficiaries

✓ Asset Monetization
  - Working with investment bankers
  - Generating monthly residual income
  - Using assets as collateral
  - Building generational wealth

✓ Certification
  - Complete understanding of the Reclaimation Process
  - Equipped to reclaim birth rights
  - Knowledge to build lasting wealth

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

RESOURCES:

Knowledge Base: https://knowledgebase.live
Pioneered by: Empress Ninti El Bey

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are now equipped with the knowledge to follow the
path to freedom and reclaim your birth rights.

Congratulations, Beneficiary!

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
  };

  const downloadCertificate = () => {
    try {
      const certificateText = generateCertificateText();
      const blob = new Blob([certificateText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Beneficiaries-Blueprint-Certificate-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Certificate Downloaded! 🎉",
        description: "Your completion certificate has been saved.",
      });
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast({
        title: "Download Error",
        description: "Unable to download certificate. Please try again.",
        variant: "destructive"
      });
    }
  };

  const downloadProgressReport = () => {
    try {
      const reportData = {
        completionDate: new Date().toISOString(),
        userProgress: {
          totalScore: userProgress.totalScore,
          completedStages: userProgress.completedStages,
          currentStage: userProgress.currentStage,
          challengeAttempts: userProgress.challengeAttempts
        },
        completedStageDetails: completedStages.map(stage => ({
          id: stage.id,
          title: stage.title,
          subtitle: stage.subtitle
        })),
        completionRate: completionPercentage,
        certificate: true
      };

      const blob = new Blob([JSON.stringify(reportData, null, 2)], { 
        type: 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Beneficiaries-Blueprint-Progress-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Progress Report Downloaded!",
        description: "Your detailed progress report has been saved.",
      });
    } catch (error) {
      console.error('Error downloading progress report:', error);
      toast({
        title: "Download Error",
        description: "Unable to download progress report. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Achievement Summary */}
      <Card className="border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-2xl text-green-900 flex items-center gap-3">
            <Trophy className="w-8 h-8 text-yellow-600" />
            Your Achievement Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-white rounded-lg border-2 border-green-200">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-2" />
              <p className="text-3xl font-bold text-green-700">{completedStages.length}/{stages.length}</p>
              <p className="text-slate-600 text-sm">Stages Completed</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border-2 border-purple-200">
              <Award className="w-12 h-12 text-purple-600 mx-auto mb-2" />
              <p className="text-3xl font-bold text-purple-700">{userProgress.totalScore}</p>
              <p className="text-slate-600 text-sm">Total Points</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border-2 border-blue-200">
              <Trophy className="w-12 h-12 text-blue-600 mx-auto mb-2" />
              <p className="text-3xl font-bold text-blue-700">{completionPercentage}%</p>
              <p className="text-slate-600 text-sm">Completion Rate</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Download Options */}
      <Card className="border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-xl text-green-900 flex items-center gap-2">
            <Award className="w-6 h-6" />
            Your Certificate is Ready!
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Button
                onClick={() => setShowCertificate(true)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg transition-colors"
                data-testid="view-certificate-btn"
              >
                <Eye className="w-5 h-5 mr-2" />
                View Certificate
              </Button>
              <p className="text-sm text-slate-600 text-center mt-2">
                Preview your certificate
              </p>
            </div>
            <div>
              <Button
                onClick={downloadCertificate}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg transition-colors"
                data-testid="download-certificate-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Certificate
              </Button>
              <p className="text-sm text-slate-600 text-center mt-2">
                Save to your device
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Certificate Preview Dialog */}
      <Dialog open={showCertificate} onOpenChange={setShowCertificate}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Award className="w-6 h-6 text-purple-700" />
              Certificate of Completion
            </DialogTitle>
          </DialogHeader>
          <div className="bg-gradient-to-br from-purple-50 via-white to-blue-50 p-8 rounded-lg border-4 border-purple-300 shadow-lg">
            {/* Certificate Header */}
            <div className="text-center border-b-2 border-purple-200 pb-6 mb-6">
              <div className="flex justify-center mb-4">
                <Trophy className="w-16 h-16 text-yellow-500" />
              </div>
              <h1 className="text-3xl font-bold text-purple-900 mb-2">
                BENEFICIARIES BLUEPRINT
              </h1>
              <h2 className="text-xl text-purple-700">
                Certificate of Completion
              </h2>
            </div>

            {/* Certificate Body */}
            <div className="text-center mb-6">
              <p className="text-lg text-slate-700 mb-4">
                This certifies that the bearer has successfully completed the
              </p>
              <p className="text-2xl font-bold text-purple-900 mb-4">
                Beneficiaries Blueprint Educational Program
              </p>
              <p className="text-lg text-slate-700">
                and has mastered the Reclaimation Process pioneered by
              </p>
              <p className="text-xl font-semibold text-purple-800 mt-2">
                Empress Ninti El Bey
              </p>
            </div>

            {/* Achievement Stats */}
            <div className="grid grid-cols-3 gap-4 my-8 py-6 border-y-2 border-purple-200">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{completedStages.length}/{stages.length}</p>
                <p className="text-sm text-slate-600">Stages Completed</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{userProgress.totalScore}</p>
                <p className="text-sm text-slate-600">Total Points</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{completionPercentage}%</p>
                <p className="text-sm text-slate-600">Completion Rate</p>
              </div>
            </div>

            {/* Knowledge Mastered */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-4 text-center">
                Knowledge Mastered
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {['The Foundation', 'Lawful Notice', 'Status Correction', 'Trust Creation', 'Asset Monetization', 'Certification'].map((topic, index) => (
                  <div key={index} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span>{topic}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Date and Footer */}
            <div className="text-center border-t-2 border-purple-200 pt-6">
              <p className="text-slate-600 mb-2">
                Date Completed: {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="text-purple-700 font-semibold">
                Congratulations, Beneficiary!
              </p>
            </div>
          </div>
          
          {/* Dialog Actions */}
          <div className="flex gap-4 mt-4">
            <Button
              onClick={() => setShowCertificate(false)}
              variant="outline"
              className="flex-1"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                downloadCertificate();
                setShowCertificate(false);
              }}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Completion Message */}
      <Card className="border-2 border-slate-200">
        <CardContent className="pt-6">
          <div className="text-center space-y-3">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
            <h3 className="text-2xl font-bold text-slate-900">
              Journey Complete!
            </h3>
            <p className="text-slate-700 max-w-2xl mx-auto leading-relaxed">
              You have successfully completed the Beneficiaries Blueprint and mastered the 
              Reclaimation Process pioneered by Empress Ninti El Bey. You are now equipped 
              with the knowledge to reclaim your birth rights and build generational wealth.
            </p>
            <p className="text-slate-600 text-sm mt-4">
              For additional resources and deeper learning, visit{' '}
              <a
                href="https://knowledgebase.live"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-700 hover:text-purple-900 font-semibold underline"
              >
                knowledgebase.live
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CertificateDownload;